/**
 * What happened when we tried to record a referral, from the error the service threw.
 *
 * A pure module with no runtime imports, because getting this mapping wrong is the difference between a
 * working feature and one that reports success while crediting nobody — and because the mapping cannot be
 * taken from the documentation.
 *
 * ⚠️ **Classify on the wire, not on the documentation — they disagree.** The proto declares an
 * already-referred call as `PERMISSION_DENIED` with application code `ALREADY_REFERRED`, and the generated
 * client's docstring repeats it; the service throws `AlreadyExistsException`, i.e. **`ALREADY_EXISTS`**,
 * which its own test pins and production confirms. Anyone reconciling this file against the proto will
 * break it.
 *
 * ⚠️ **`PERMISSION_DENIED` is TWO different things**, and conflating them is the expensive mistake: the
 * service throws it for a self-referral, and the platform throws it for a caller that lacks the
 * `mark_referral` permission. Treating all of it as "self-referral, which is fine" would make a
 * completely unauthorized integration report a 100% success rate. They are told apart by the permission
 * name appearing in the platform's message.
 *
 * ⚠️ **`FAILED_PRECONDITION` is also two things**, and they need opposite handling: the referee's
 * recipient not being visible yet, or lacking a phone, is transient (keep the stored id and try on a later
 * verification); the REFERRER not existing is permanent (drop it, it can never work).
 */
export type ReferralOutcome =
  /** Written. The sharer is credited. */
  | 'recorded'
  /** This phone was already referred, ever, by anyone. Terminal, and documented as effectively success. */
  | 'already'
  /** The caller used their own link. Terminal. */
  | 'self'
  /** This app may not call `mark_referral`. Not the user's problem, and NOT a success. */
  | 'denied'
  /** Nothing to record: the value was not a metasite id, or the referrer does not exist. Terminal. */
  | 'invalid'
  /** Could not be recorded yet — retry on a later phone verification. */
  | 'retry'
  /** An unrecognised failure. Kept for retry, and worth an error report. */
  | 'failed';

/** The outcomes after which the stored referrer id is no longer worth keeping. */
export const TERMINAL_OUTCOMES: ReadonlySet<ReferralOutcome> = new Set<ReferralOutcome>(['recorded', 'already', 'self', 'invalid']);

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err ?? ''));

export function classifyReferralOutcome(err: unknown): ReferralOutcome {
  const text = message(err);

  if (text.includes('ALREADY_EXISTS') || text.includes('already been referred')) return 'already';

  // Checked ahead of the status, not inside it: the platform's refusal has been observed both with and
  // without a `PERMISSION_DENIED` prefix, and a missing grant misread as anything else is the failure
  // that would make a dead integration look healthy.
  if (text.includes('mark_referral') || text.includes('InsufficientPermissions')) return 'denied';

  if (text.includes('PERMISSION_DENIED') || text.includes('FORBIDDEN')) {
    if (text.includes('cannot refer themselves')) return 'self';
    // A metasite missing from the request context is ours to fix, not the user's, and not a success.
    return 'denied';
  }

  if (text.includes('FAILED_PRECONDITION')) {
    // "Referrer Recipient not found for metaSiteId …" — the sharer does not exist. Nothing to wait for.
    if (text.includes('Referrer Recipient not found')) return 'invalid';
    // "Referee Recipient not found …" / "Recipient must have both email and phone …" — the caller's own
    // record is not ready yet. Both are worth another attempt later.
    return 'retry';
  }

  if (text.includes('INVALID_ARGUMENT')) return 'invalid';

  return 'failed';
}
